import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import api from '../../../../api';
import CurrencyInput from 'react-currency-input-field';
import { AppSelect } from '../../../../components/common/select';
import useModalEffect from '../../../../hooks/useModalEffect';

const ItemForm = ({ isOpen, onClose, onSubmit, editingItem = null, invoiceId, currencyCode = 'IDR', invoiceDate = null }) => {
  useModalEffect(isOpen);
    const [formData, setFormData] = useState({
        description: '',
        quantity: '1',
        unitPrice: '',
        discount: '',
        taxed: false,
        currencyCode: currencyCode || 'IDR',
        exchangeRateAtCreation: 1
    });

    const [formErrors, setFormErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currencyOptions, setCurrencyOptions] = useState([]);
    const [currencyRates, setCurrencyRates] = useState([]);

    // ✅ MULTI-CURRENCY: Helper functions untuk currency
    const getCurrencySymbol = (currencyCode) => {
        switch (currencyCode) {
            case 'USD': return '$';
            case 'EUR': return '€';
            case 'JPY': return '¥';
            case 'GBP': return '£';
            case 'SGD': return 'S$';
            case 'AUD': return 'A$';
            case 'IDR': return 'Rp';
            default: return 'Rp';
        }
    };

    const getCurrencyPrefix = (currencyCode) => {
        return getCurrencySymbol(currencyCode) + ' ';
    };

    const getEffectiveCurrencyRate = (selectedCurrencyCode) => {
        const currency = selectedCurrencyCode || formData.currencyCode || currencyCode || 'IDR';
        if (!currency || currency.toUpperCase() === 'IDR') return 1;

        const effectiveDate = invoiceDate ? new Date(invoiceDate) : new Date();
        if (Number.isNaN(effectiveDate.getTime())) return 1;

        const rateRecord = currencyRates.find((rate) => {
            const code = (rate.currency_code || rate.currencyCode || '').toUpperCase();
            const startDate = rate.start_date ? new Date(rate.start_date) : null;
            const endDate = rate.end_date ? new Date(rate.end_date) : null;
            return code === currency.toUpperCase() && startDate && effectiveDate >= startDate && (!endDate || effectiveDate <= endDate);
        });

        return rateRecord ? Number(rateRecord.exchange_rate || rateRecord.exchangeRate || 1) : 1;
    };

    useEffect(() => {
        const loadCurrencies = async () => {
            try {
                const [currencyResponse, rateResponse] = await Promise.all([
                    api.get('/crm/currencies').catch(() => ({ data: [] })),
                    api.get('/crm/currency-rates').catch(() => ({ data: [] }))
                ]);

                const currencies = currencyResponse.data || [];
                setCurrencyOptions(currencies.map(currency => ({
                    value: currency.currencyCode || 'IDR',
                    label: `${currency.currencyCode} - ${currency.currencyName}`,
                    name: currency.currencyName
                })));

                const rates = Array.isArray(rateResponse.data) ? rateResponse.data : [];
                setCurrencyRates(rates);
            } catch (error) {
                setCurrencyOptions([]);
                setCurrencyRates([]);
            }
        };

        loadCurrencies();
    }, [invoiceDate]);

    const formatCurrency = (amount, currencyParam = null) => {
        const currency = currencyParam || currencyCode;
        if (!amount || amount === 0) return `${getCurrencyPrefix(currency)}0`;
        
        const symbol = getCurrencySymbol(currency);
        const formattedAmount = Number(amount).toLocaleString('id-ID');
        return `${symbol} ${formattedAmount}`;
    };

    // ✅ MULTI-CURRENCY: Calculate total amount preview
    const calculateTotalAmount = () => {
        const unitPriceValue = formData.unitPrice ? parseFloat(formData.unitPrice.replace(/[^\d.-]/g, '')) : 0;
        const quantity = parseFloat(formData.quantity) || 1;
        const discountPercentage = formData.discount ? parseFloat(formData.discount) : 0;
        
        const subtotal = unitPriceValue * quantity;
        const discountAmount = subtotal * (discountPercentage / 100);
        const subtotalAfterDiscount = subtotal - discountAmount;
        
        // Calculate tax amount if item is taxed
        const taxAmount = formData.taxed ? subtotalAfterDiscount * 0.11 : 0; // Assuming 11% tax rate
        
        const total = subtotalAfterDiscount + taxAmount;
        const exchangeRate = getEffectiveCurrencyRate(formData.currencyCode || currencyCode);
        const totalInBaseCurrency = total * exchangeRate;
        
        return {
            subtotal,
            discountAmount,
            subtotalAfterDiscount,
            taxAmount,
            total,
            exchangeRate,
            totalInBaseCurrency
        };
    };

    // Reset form data ketika editingItem berubah
    useEffect(() => {
        if (editingItem) {
            // Convert discount amount back to percentage for editing
            const unitPrice = editingItem.unitPrice || editingItem.rate || 0;
            const discountAmount = editingItem.discount || 0;
            const discountPercentage = unitPrice > 0 ? ((discountAmount / unitPrice) * 100).toFixed(2) : '0';

            const newFormData = {
                description: editingItem.description || '',
                quantity: editingItem.quantity?.toString() || '1',
                unitPrice: unitPrice?.toString() || '',
                discount: discountPercentage,
                taxed: editingItem.taxed || editingItem.isTaxed || false,
                currencyCode: editingItem.currencyCode || currencyCode || 'IDR',
                exchangeRateAtCreation: editingItem.exchangeRateAtCreation || 1
            };
            
            setFormData(newFormData);
        } else {
            // Reset form untuk add new item
            setFormData({
                description: '',
                quantity: '1',
                unitPrice: '',
                discount: '',
                taxed: false,
                currencyCode: currencyCode || 'IDR',
                exchangeRateAtCreation: 1
            });
        }
        setFormErrors({});
    }, [editingItem]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle discount percentage validation
        if (name === 'discount' && value !== '') {
            const numValue = parseFloat(value);
            if (numValue > 100) {
                // Don't allow values over 100
                return;
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCurrencyChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.description) errors.description = 'Description is required';
        if (!formData.quantity) errors.quantity = 'Quantity is required';
        if (!formData.unitPrice) errors.unitPrice = 'Unit price is required';
        
        // Validate discount percentage
        if (formData.discount && (parseFloat(formData.discount) < 0 || parseFloat(formData.discount) > 100)) {
            errors.discount = 'Discount must be between 0% and 100%';
        }
        
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setLoading(true);
        try {
            // Calculate discount amount from percentage
            const unitPriceValue = formData.unitPrice ? parseFloat(formData.unitPrice.replace(/[^\d.-]/g, '')) : 0;
            const discountPercentage = formData.discount ? parseFloat(formData.discount) : 0;
            const discountAmount = unitPriceValue * (discountPercentage / 100);

            const payload = {
                invoiceId: invoiceId,
                description: formData.description,
                quantity: parseFloat(formData.quantity),
                unitPrice: unitPriceValue,
                discount: discountAmount, // Send calculated discount amount in currency
                isTaxed: formData.taxed,
                currencyCode: formData.currencyCode || currencyCode || 'IDR',
                baseCurrencyCode: 'IDR',
                exchangeRateAtCreation: calculateTotalAmount().exchangeRate || 1
            };

            let response;
            
            if (editingItem && editingItem.id) {
                // Update existing item
                response = await api.put(`/crm/invoice-items/${editingItem.id}`, payload);
            } else {
                // Create new item
                response = await api.post('/crm/invoice-items', payload);
            }

            // Call parent onSubmit with the response data
            onSubmit(response.data);
            onClose();
            
            // Reset form
            setFormData({
                description: '',
                quantity: '1',
                unitPrice: '',
                discount: '',
                taxed: false,
                currencyCode: currencyCode || 'IDR',
                exchangeRateAtCreation: 1
            });
            setFormErrors({});

        } catch (error) {
            console.error('Error saving invoice item:', error);
            
            if (error.response?.data?.message) {
                setFormErrors({ general: error.response.data.message });
            } else if (error.response?.status === 400) {
                setFormErrors({ general: 'Invalid data provided. Please check your input.' });
            } else if (error.response?.status === 404) {
                setFormErrors({ general: 'Invoice not found.' });
            } else {
                setFormErrors({ general: 'An error occurred while saving the item. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    // Modal show/hide effect
    ;

    // Cleanup effect when component unmounts
    useEffect(() => {
        return () => {
            // Clean up any remaining modal states when component unmounts
            const cleanupModal = () => {
                // Remove modal scroll lock
            };
            
            cleanupModal();
        };
    }, []);


    // Cleanup effect: reset modal body state when component unmounts.
    useEffect(() => {
        return () => {
            // React owns the backdrop DOM (createPortal); only reset body state here.
            // Remove modal-open class from body
        };
    }, []);

    const handleClose = () => {
        
        
        setFormData({
            description: '',
            quantity: '1',
            unitPrice: '',
            discount: '',
            taxed: false,
            currencyCode: currencyCode || 'IDR',
            exchangeRateAtCreation: 1
        });
        setFormErrors({});
        onClose();
    };

    return (
        <>
            <div
                className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
                id="modal-item-form"
                tabIndex="-1"
                role="dialog"
            >
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {editingItem && editingItem.id ? 'Edit Item' : 'Add Item'}
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                {/* General Error Display */}
                                {formErrors.general && (
                                    <div className="alert alert-danger" role="alert">
                                        {formErrors.general}
                                    </div>
                                )}

                                <div className="row mb-3">
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Description <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Enter item description"
                                            disabled={loading}
                                        />
                                        {formErrors.description && (
                                            <div className="invalid-feedback">{formErrors.description}</div>
                                        )}
                                    </div>

                                    <div className="col-12 col-md-6 mb-3">
                                        <label className="form-label">Currency <span className="text-danger">*</span></label>
                                        <AppSelect
                                            name="currencyCode"
                                            value={formData.currencyCode || currencyCode || 'IDR'}
                                            onChange={(name, value) => handleInputChange({ target: { name, value } })}
                                            options={currencyOptions}
                                            placeholder="Select currency"
                                            valueField="value"
                                            labelField="label"
                                            searchField="label"
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="col-12 col-md-6 mb-3">
                                        <label className="form-label">Unit Price <span className="text-danger">*</span></label>
                                        <CurrencyInput
                                            className={`form-control ${formErrors.unitPrice ? 'is-invalid' : ''}`}
                                            name="unitPrice"
                                            value={formData.unitPrice}
                                            onValueChange={(value) => handleCurrencyChange('unitPrice', value)}
                                            placeholder="0"
                                            decimalsLimit={(formData.currencyCode || currencyCode) === 'JPY' ? 0 : 2}
                                            prefix={getCurrencyPrefix(formData.currencyCode || currencyCode)}
                                            allowNegativeValue={false}
                                            disabled={loading}
                                        />
                                        {formErrors.unitPrice && (
                                            <div className="invalid-feedback">{formErrors.unitPrice}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-link link-secondary" 
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary ms-auto"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            {editingItem && editingItem.id ? 'Saving...' : 'Adding...'}
                                        </>
                                    ) : (
                                        editingItem && editingItem.id ? 'Update' : 'Save'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ItemForm; 